import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// Removed Chakra imports - using Tailwind classes instead
import * as echarts from 'echarts/core';
import ReactECharts from 'echarts-for-react';
import { BarSeriesOption } from 'echarts/charts';
import { MachineStatusData, useLazyGetMachineStatusStatisticsQuery } from '@/store/api/statisticsApi';
import {
  calculatePercentage,
  extractYears,
  filterDataBySpecificMonth,
  getOption,
  getSeries,
  groupDaysIntoColumns,
  mergeEntitiesByMonth,
  LATEST_YEAR_OPTION,
} from './machine-status-chart/utils';
import { MachineStatusChartTitle } from './machine-status-chart/MachineStatusChartTitle';
import { ActiveMonthLink } from './machine-status-chart/ActiveMonthLink';
import { DropdownOptionGroupMenu } from '@/components/uiKit/DropdownOptionGroupMenu';
import { Skeleton } from '@/components/ui/skeleton';
import { BaseDropdownFilterMenu } from '@/components/uiKit/Dropdown';

const TITLE_ROW_HEIGHT = 53;

export const MachineStatusChartNew = ({ healthOverviewRowHeight }: any) => {
  const { siteId } = useParams();
  const [fetchMachineStatusStatistics, { data: machineStatusData }] = useLazyGetMachineStatusStatisticsQuery({});
  const [activeMonth, setActiveMonth] = useState<string | null>(null);
  const [dataByMonth, setDataByMonth] = useState<MachineStatusData>([]);
  const [seriesByMonth, setSeriesByMonth] = useState<Array<BarSeriesOption>>([]);
  const [series, setSeries] = useState<Array<BarSeriesOption> | undefined>();
  const [dataset, updateDataset] = useState<MachineStatusData>([]);
  const [yearOptions, setYearOptions] = useState<Array<string>>([]);
  const [selectedYear, setSelectedYear] = useState<string>();

  const onChartClick = (e: { name?: string }) => {
    const { name } = e;
    if (!activeMonth && name) {
      setActiveMonth(name);
    }
  };

  const resetData = () => {
    setActiveMonth(null);
    setSeries(seriesByMonth);
    updateDataset(dataByMonth);
  };

  const prepareTop7ChartData = (selectedYear?: string) => {
    const availableYears = extractYears(machineStatusData);
    let availableSeries;

    const dataGroupedByMonth = mergeEntitiesByMonth(
      machineStatusData,
      availableYears,
      selectedYear || LATEST_YEAR_OPTION
    );

    if (dataGroupedByMonth.length) {
      const percentageByMonth = calculatePercentage(dataGroupedByMonth);
      availableSeries = getSeries(percentageByMonth, dataGroupedByMonth);
    }

    setYearOptions(availableYears.map(String));
    setDataByMonth(dataGroupedByMonth);
    setSeriesByMonth(availableSeries || []);
    setSeries(availableSeries || []);
    updateDataset(dataGroupedByMonth);
  };

  useEffect(() => (selectedYear ? prepareTop7ChartData(selectedYear) : undefined), [selectedYear]);

  useEffect(() => {
    if (machineStatusData) {
      prepareTop7ChartData();
    }
  }, [machineStatusData]);

  useEffect(() => {
    if (activeMonth && machineStatusData) {
      const filteredDataByMonth = filterDataBySpecificMonth(activeMonth, machineStatusData);
      const groupedByColumns = groupDaysIntoColumns(filteredDataByMonth);
      const percentageByColumns = calculatePercentage(groupedByColumns);
      const series = getSeries(percentageByColumns, groupedByColumns);

      setSeries(series);
      updateDataset(groupedByColumns);
    }
  }, [activeMonth]);

  useEffect(() => {
    if (machineStatusData) {
      prepareTop7ChartData();
    }
  }, [machineStatusData]);

  useEffect(() => {
    fetchMachineStatusStatistics({ site_id: siteId });
  }, [siteId]);

  return (
    <div className="w-full bg-white rounded-md">
      {series ? (
        <>
          <div className="flex justify-between items-center pb-2 -mt-1">
            <div className="flex items-center gap-2">
              <MachineStatusChartTitle activeMonth={activeMonth} onClick={resetData} />
              <ActiveMonthLink activeMonth={activeMonth} />
            </div>
            {yearOptions && !activeMonth && series.length ? (
              <BaseDropdownFilterMenu
                defaultSelected={{ Period: [LATEST_YEAR_OPTION] }}
                multiSelect={false}
                closeOnSelect={true}
                filterLable="Period Selection"
                onFilterChange={(data: any) => {
                  setSelectedYear(data.Period[0] as string);
                  resetData();
                }}
                data={[
                  {
                    menuLabel: 'Period',
                    menuItems: [LATEST_YEAR_OPTION, ...yearOptions].map((el) => {
                      return { id: el, label: el };
                    }),
                  },
                ]}
              />
            ) : null}
          </div>
          {series.length ? (
            <ReactECharts
              style={{
                height: healthOverviewRowHeight ? healthOverviewRowHeight - TITLE_ROW_HEIGHT : 215,
              }}
              echarts={echarts}
              option={getOption(
                dataset,
                series,
                activeMonth,
                healthOverviewRowHeight ? healthOverviewRowHeight - 130 : 140
              )}
              onEvents={{
                click: onChartClick,
              }}
            />
          ) : (
            <div
              className="w-full px-4 text-center text-gray-500 text-sm flex items-center justify-center"
              style={{ height: healthOverviewRowHeight ? healthOverviewRowHeight - TITLE_ROW_HEIGHT : 200 }}
            >
              Insufficient data available to generate the chart
            </div>
          )}
        </>
      ) : (
        <Skeleton className="h-[240px] w-full" />
      )}
    </div>
  );
};
